// ============================================================================
// DrillDrawer — generic right-side slide-in panel for chart drill-down
// ============================================================================
// Renders a backdrop + a fixed right-anchored drawer carrying a header (title +
// filter chips + close), a body (any React node — typically a SyutGrid), and an
// optional footer. Mounted via createPortal so it escapes the dashboard's
// stacking context and overlays correctly above sticky widget headers.
//
// Closes on Escape, backdrop click, or the X button.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import IconSystem from 'icon-collection/icon-systems';

export interface DrillFilterChip {
  label: string;        // "Age: 91-180 days"
  /** Optional colour for the chip's left-strip (matches the chart segment). */
  colour?: string;
}

interface Props {
  open: boolean;
  title: string;
  /** Subtitle below the title — typically a count + amount summary. */
  subtitle?: string;
  filterChips?: DrillFilterChip[];
  /** Body content — usually a table or a SyutGrid. */
  children: React.ReactNode;
  /** Optional footer slot (e.g., "Open Full Report" button). */
  footer?: React.ReactNode;
  onClose: () => void;
}

const DrillDrawer: React.FC<Props> = ({
  open, title, subtitle, filterChips, children, footer, onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <>
      <div className="drill-drawer-backdrop" onClick={onClose} />
      <aside className="drill-drawer" role="dialog" aria-modal="true">
        <header className="drill-drawer-header">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="drill-drawer-title">{title}</div>
            {subtitle && <div className="drill-drawer-subtitle">{subtitle}</div>}
            {filterChips && filterChips.length > 0 && (
              <div className="drill-drawer-chips">
                {filterChips.map((c, i) => (
                  <span key={i} className="drill-drawer-chip">
                    {c.colour && <span className="drill-drawer-chip-strip" style={{ background: c.colour }} />}
                    {c.label}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button className="drill-drawer-close" onClick={onClose} aria-label="Close" title="Close (Esc)">
            <IconSystem.x size={18} />
          </button>
        </header>

        <div className="drill-drawer-body">{children}</div>

        {footer && <footer className="drill-drawer-footer">{footer}</footer>}
      </aside>
    </>,
    document.body,
  );
};

export default DrillDrawer;
